/*
  Mobile sticky add-to-cart bar.
  Proxies clicks to the real product form submit button so all existing
  behavior (cart drawer, validation, tracking) is reused. Shows only after
  the buy buttons scroll out of view (IntersectionObserver); mobile-only via CSS.
*/
class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    // Re-home to <body>: position:fixed breaks inside any ancestor with a
    // transform/filter/contain, and iOS Safari is especially sensitive.
    // appendChild re-fires connectedCallback with body as parent.
    if (this.parentElement !== document.body) {
      document.body.querySelectorAll('sticky-atc-bar').forEach((el) => {
        if (el !== this) el.remove();
      });
      document.body.appendChild(this);
      return;
    }

    const sectionId = this.dataset.sectionId;
    this.realButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!this.realButton) return; // no purchasable form on this page

    this.button = this.querySelector('[data-sticky-submit]');
    this.priceTarget = this.querySelector('[data-sticky-price]');
    this.priceSource = document.getElementById(`price-${sectionId}`);

    this.syncFromProduct();

    this.button.addEventListener('click', () => this.realButton.click());

    const buyButtons = this.realButton.closest('.product-form__buttons') || this.realButton;
    this.observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', scrolledPast);
    });
    this.observer.observe(buyButtons);

    // Variant changes re-render the price and toggle the real button: mirror both.
    this.buttonObserver = new MutationObserver(() => this.syncFromProduct());
    this.buttonObserver.observe(this.realButton, { attributes: true, attributeFilter: ['disabled'] });
    if (this.priceSource) {
      this.priceObserver = new MutationObserver(() => this.syncPrice());
      this.priceObserver.observe(this.priceSource, { childList: true, subtree: true });
    }

    // The bar sits above app widgets, so it must yield to any overlay
    // (cart drawer, menu drawer, modals). They all set body.overflow-hidden.
    const syncOverlayState = () =>
      this.classList.toggle('is-suppressed', document.body.classList.contains('overflow-hidden'));
    syncOverlayState();
    this.drawerObserver = new MutationObserver(syncOverlayState);
    this.drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Belt and braces: hide instantly on our own click — the body observer
    // restores the correct state when the drawer closes.
    this.button.addEventListener('click', () => this.classList.add('is-suppressed'));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.buttonObserver) this.buttonObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
    if (this.drawerObserver) this.drawerObserver.disconnect();
  }

  syncPrice() {
    if (this.priceSource && this.priceTarget) this.priceTarget.innerHTML = this.priceSource.innerHTML;
  }

  syncFromProduct() {
    this.syncPrice();
    const disabled = this.realButton.hasAttribute('disabled');
    this.button.toggleAttribute('disabled', disabled);
    const label = this.realButton.querySelector('span');
    if (label && label.textContent.trim()) this.button.textContent = label.textContent.trim();
  }
}

customElements.define('sticky-atc-bar', StickyAtcBar);
